import { handleApiRequest, Loading } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountJobPositionAssignmentStatusChoices,
  AuthAccountOrganizationJobPositionStatusChoices,
  JobSeekerType,
  useAcceptJobSuggestionWithIdMutation,
  useGetJobSuggestionsQuery,
} from "@graphql/generated";
import { addIsConfirmedToJobSuggestionResult, JobSuggestionResults } from "@modules/JobPosition/utils";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import CandidatePoolTable from "./components/CandidatePoolTable";
import JobSeekersAwaitingList from "./components/JobSeekersAwaitingList";

export type UserSuggestionType = {
  suggestionId: string;
  user: JobSeekerType;
  status: AuthAccountJobPositionAssignmentStatusChoices;
  matchExplanation?: string;
  matchScore?: number;
  recommendation?: number;
  seniorityMatch?: number;
  skillMatch?: number;
  strengths?: string[];
  considerations?: string[];
  confidenceLevel?: number;
  experienceMatch?: number;
  jobPositionAssignmentId?: string;
  createdAt?: string;
};

interface JobSuggestionTabContentProps {
  status?: AuthAccountOrganizationJobPositionStatusChoices;
}

const JobSuggestionTabContent = ({ status }: JobSuggestionTabContentProps) => {
  const { id } = useParams();

  const [acceptJobSuggestionLoadingId, setAcceptJobSuggestionLoadingId] = useState<string>();

  const [acceptJobSuggestion, { loading: acceptJobSuggestionLoading }] = useAcceptJobSuggestionWithIdMutation();

  const {
    data,
    loading: jobSuggestionsLoading,
    refetch,
  } = useGetJobSuggestionsQuery({
    variables: {
      jobPositionId: id ?? "",
    },
    skip: !id,
  });

  const handleAcceptSuggestion = (jobSuggestionId: string) => {
    setAcceptJobSuggestionLoadingId(jobSuggestionId);
    handleApiRequest({
      apiFunction: acceptJobSuggestion,
      variables: { jobSuggestionResultId: jobSuggestionId },
      componentName: "JobSuggestionTabContent",
      actionName: "handleAcceptSuggestion",
      onSuccess: () => {
        if (!id) toast.error("Failed to fetch job position");
        refetch({
          jobPositionId: id ?? "",
        });
      },
    });
  };

  const suggestionEdges = addIsConfirmedToJobSuggestionResult(
    data?.admin?.jobSuggestionResult?.edges[0]?.node?.items?.edges ?? [],
  );

  const mapToUserSuggestion = (suggestion: JobSuggestionResults[number]): UserSuggestionType => ({
    suggestionId: suggestion?.node?.id ?? "",
    user: suggestion?.node?.user as JobSeekerType,
    status: suggestion?.node?.jobPositionAssignment?.status as AuthAccountJobPositionAssignmentStatusChoices,
    matchExplanation: suggestion?.node?.matchExplanation || undefined,
    matchScore: suggestion?.node?.matchScore || undefined,
    recommendation: suggestion?.node?.recommendation || undefined,
    seniorityMatch: suggestion?.node?.seniorityMatch || undefined,
    skillMatch: suggestion?.node?.skillMatch || undefined,
    strengths: suggestion?.node?.strengths || undefined,
    considerations: suggestion?.node?.considerations || undefined,
    confidenceLevel: suggestion?.node?.confidenceLevel || undefined,
    experienceMatch: suggestion?.node?.experienceMatch || undefined,
    jobPositionAssignmentId: suggestion?.node?.jobPositionAssignment?.id || undefined,
    createdAt: suggestion?.node?.jobPositionAssignment?.createdAt || undefined,
  });

  const jobSeekersAwaiting = suggestionEdges
    ?.filter((item) => item?.node?.isConfirmed)
    ?.map((item) => mapToUserSuggestion(item as JobSuggestionResults[number]));

  const remainingJobSeekers = suggestionEdges
    ?.filter((item) => !item?.node?.isAssigned)
    ?.map((item) => mapToUserSuggestion(item as JobSuggestionResults[number]));

  if (jobSuggestionsLoading) return <Loading />;

  return (
    <div className="flex flex-col gap-4">
      <JobSeekersAwaitingList jobSeekers={jobSeekersAwaiting || []} onRefetch={refetch} />
      <CandidatePoolTable
        jobSeekers={remainingJobSeekers || []}
        onAcceptSuggestion={handleAcceptSuggestion}
        loading={acceptJobSuggestionLoading}
        loadingId={acceptJobSuggestionLoadingId}
        jobPositionId={id ?? ""}
        onTriggerJobSuggestionSuccess={refetch}
        status={status}
      />
    </div>
  );
};

export default JobSuggestionTabContent;
